import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = loading, false = anon, {..} = user
    const [error, setError] = useState("");

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            setUser(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const login = async (email, password) => {
        setError("");
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data.access_token) localStorage.setItem("fz_token", data.access_token);
            setUser(data.user);
            return data.user;
        } catch (e) {
            const msg = formatApiError(e);
            setError(msg);
            throw new Error(msg);
        }
    };

    const register = async (email, password, name) => {
        setError("");
        try {
            const { data } = await api.post("/auth/register", { email, password, name });
            if (data.access_token) localStorage.setItem("fz_token", data.access_token);
            setUser(data.user);
            return data.user;
        } catch (e) {
            const msg = formatApiError(e);
            setError(msg);
            throw new Error(msg);
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            // Non-blocking: even if the server-side logout call fails,
            // we still clear local state so the user is logged out client-side.
            console.warn("Logout endpoint failed, clearing client state anyway:", e?.message || e);
        }
        localStorage.removeItem("fz_token");
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, setUser, error, login, register, logout, refresh }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
